/**
 * Chutt... - Segment Wheel (Multi-Ring Donut)
 * Anneau exterieur: plateformes (Twitter, News, Facebook, etc.)
 * Anneau interieur: sentiment (positif/neutre/negatif)
 * Centre: total mentions en gros texte.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { colors, meltwaterPalette, platformColors } from '../../styles/colors';

const PLATFORM_COLORS = platformColors;

const PLATFORM_LABELS = {
  twitter: 'Twitter/X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  news: 'Actualites',
  forum: 'Forums',
  web: 'Web',
  youtube: 'YouTube',
  google_reviews: 'Google',
};

function SegmentWheel({ platformData, sentimentData, totalMentions }) {
  // Default fallback data
  const platforms = (platformData || []).map((p, i) => ({
    ...p,
    name: PLATFORM_LABELS[p.platform] || p.platform || 'Autre',
    value: p.count || 0,
    color: PLATFORM_COLORS[p.platform] || meltwaterPalette.platforms[i % meltwaterPalette.platforms.length],
  })).filter(p => p.value > 0);

  const sentiments = sentimentData || [
    { name: 'Positif', value: 0, color: colors.success },
    { name: 'Neutre', value: 0, color: colors.gray300 },
    { name: 'Negatif', value: 0, color: colors.danger },
  ];

  const total = totalMentions || platforms.reduce((sum, p) => sum + p.value, 0);

  if (total === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
        <Typography sx={{ color: colors.gray400 }}>Aucune donnee</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          {/* Inner ring: sentiment */}
          <Pie
            data={sentiments.filter(s => s.value > 0)}
            innerRadius={55}
            outerRadius={85}
            paddingAngle={2}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            animationBegin={0}
            animationDuration={800}
            animationEasing="ease-out"
          >
            {sentiments.filter(s => s.value > 0).map((entry, i) => (
              <Cell key={`sent-${i}`} fill={entry.color} stroke="none" />
            ))}
          </Pie>
          {/* Outer ring: platforms */}
          <Pie
            data={platforms}
            innerRadius={95}
            outerRadius={125}
            paddingAngle={1}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            animationBegin={200}
            animationDuration={800}
            animationEasing="ease-out"
          >
            {platforms.map((entry, i) => (
              <Cell key={`plat-${i}`} fill={entry.color} stroke="none" />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Center label - positioned absolutely on top of the chart */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.75rem',
            fontWeight: 800,
            color: colors.charcoal,
            fontFamily: '"DM Sans", sans-serif',
            lineHeight: 1,
          }}
        >
          {total.toLocaleString('fr-FR')}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: colors.gray400, mt: 0.3 }}>
          mentions
        </Typography>
      </Box>

      {/* Mini legend below */}
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
        {platforms.slice(0, 5).map((p) => (
          <Box key={p.platform} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography sx={{ fontSize: '0.7rem', color: colors.gray500 }}>{p.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default SegmentWheel;
